import { useEffect, useState } from "react";

import Button from "../../../components/common/Button";
import Input from "../../../components/common/Input";
import Select from "../../../components/common/Select";
import ErrorMessage from "../../../components/common/ErrorMessage";

import OfferItemForm from "./OfferItemForm";
import OfferItemsTable from "./OfferItemsTable";

import useOfferings from "../../offerings/hooks/useOfferings";

import { DEFAULT_CURRENCY } from "../constants/offer.constants";
import { validateCreateOffer } from "../validation/offer.validation";

const createEmptyItem = () => ({
  offeringId: "",
  pricingPlanId: "",
  description: "",
  quantity: 1,
  unitPrice: "",
  discountAmount: 0,
  taxAmount: 0,
});

const OfferForm = ({
  initialData = null,
  queries = [],
  offerings = [],
  onSubmit,
  loading = false,
  serverErrors = {},
  submitLabel = "Create Offer",
}) => {
  /*
  |--------------------------------------------------------------------------
  | OFFERING / PRICING HOOK
  |--------------------------------------------------------------------------
  */

  const {
    fetchPricingPlans,
  } = useOfferings();

  /*
  |--------------------------------------------------------------------------
  | FORM
  |--------------------------------------------------------------------------
  */

  const [form, setForm] = useState({
    queryId:
      initialData?.queryId ||
      initialData?.query_id ||
      "",

    discountAmount:
      initialData?.discountAmount ??
      initialData?.discount_amount ??
      0,

    taxAmount:
      initialData?.taxAmount ??
      initialData?.tax_amount ??
      0,

    currency:
      initialData?.currency ||
      DEFAULT_CURRENCY,

    validUntil:
      initialData?.validUntil ||
      initialData?.valid_until ||
      "",

    notes:
      initialData?.notes || "",

    items:
      initialData?.items?.length
        ? initialData.items.map(
            (item) => ({
              offeringId:
                item?.offeringId ||
                item?.offering_id ||
                "",

              pricingPlanId:
                item?.pricingPlanId ||
                item?.pricing_plan_id ||
                "",

              description:
                item?.description ||
                "",

              quantity:
                item?.quantity ?? 1,

              unitPrice:
                item?.unitPrice ??
                item?.unit_price ??
                "",

              discountAmount:
                item?.discountAmount ??
                item?.discount_amount ??
                0,

              taxAmount:
                item?.taxAmount ??
                item?.tax_amount ??
                0,
            })
          )
        : [createEmptyItem()],
  });

  const [errors, setErrors] =
    useState({});

  /*
  |--------------------------------------------------------------------------
  | UPDATE INITIAL DATA
  |--------------------------------------------------------------------------
  */

  useEffect(() => {
    if (!initialData) {
      return;
    }

    setForm({
      queryId:
        initialData?.queryId ||
        initialData?.query_id ||
        "",

      discountAmount:
        initialData?.discountAmount ??
        initialData?.discount_amount ??
        0,

      taxAmount:
        initialData?.taxAmount ??
        initialData?.tax_amount ??
        0,

      currency:
        initialData?.currency ||
        DEFAULT_CURRENCY,

      validUntil:
        initialData?.validUntil ||
        initialData?.valid_until ||
        "",

      notes:
        initialData?.notes ||
        "",

      items:
        initialData?.items?.length
          ? initialData.items.map(
              (item) => ({
                offeringId:
                  item?.offeringId ||
                  item?.offering_id ||
                  "",

                pricingPlanId:
                  item?.pricingPlanId ||
                  item?.pricing_plan_id ||
                  "",

                description:
                  item?.description ||
                  "",

                quantity:
                  item?.quantity ?? 1,

                unitPrice:
                  item?.unitPrice ??
                  item?.unit_price ??
                  "",

                discountAmount:
                  item?.discountAmount ??
                  item?.discount_amount ??
                  0,

                taxAmount:
                  item?.taxAmount ??
                  item?.tax_amount ??
                  0,
              })
            )
          : [createEmptyItem()],
    });
  }, [initialData]);

  /*
  |--------------------------------------------------------------------------
  | NORMALIZE QUERIES
  |--------------------------------------------------------------------------
  */

  const queryOptions =
    (
      Array.isArray(queries)
        ? queries
        : queries?.data || []
    )
      .map((query) => {
        const id =
          query?.id ??
          query?._id ??
          query?.queryId ??
          query?.query_id;

        return {
          value: id
            ? String(id)
            : "",
          label:
            query?.subject ||
            query?.message ||
            `Query ${id || ""}`,
        };
      })
      .filter(
        (query) => query.value
      );

  /*
  |--------------------------------------------------------------------------
  | NORMALIZE OFFERINGS
  |--------------------------------------------------------------------------
  */

  const offeringOptions =
    (
      Array.isArray(offerings)
        ? offerings
        : offerings?.data || []
    )
      .map((offering) => {
        const id =
          offering?.id ??
          offering?._id ??
          offering?.offeringId ??
          offering?.offering_id;

        return {
          value: id
            ? String(id)
            : "",

          label:
            offering?.name ||
            offering?.title ||
            offering?.code ||
            `Offering ${id || ""}`,
        };
      })
      .filter(
        (offering) =>
          offering.value
      );

  /*
  |--------------------------------------------------------------------------
  | FORM CHANGE
  |--------------------------------------------------------------------------
  */

  const handleChange = (
    event
  ) => {
    const {
      name,
      value,
    } = event.target;

    setForm(
      (previous) => ({
        ...previous,
        [name]: value,
      })
    );

    setErrors(
      (previous) => ({
        ...previous,
        [name]: undefined,
      })
    );
  };

  /*
  |--------------------------------------------------------------------------
  | ITEM CHANGE
  |--------------------------------------------------------------------------
  */

  const handleItemChange = (
    index,
    item
  ) => {
    setForm(
      (previous) => ({
        ...previous,

        items:
          previous.items.map(
            (
              current,
              itemIndex
            ) =>
              itemIndex === index
                ? item
                : current
          ),
      })
    );
  };

  /*
  |--------------------------------------------------------------------------
  | ADD ITEM
  |--------------------------------------------------------------------------
  */

  const addItem = () => {
    setForm(
      (previous) => ({
        ...previous,

        items: [
          ...previous.items,
          createEmptyItem(),
        ],
      })
    );
  };

  /*
  |--------------------------------------------------------------------------
  | REMOVE ITEM
  |--------------------------------------------------------------------------
  */

  const removeItem = (
    index
  ) => {
    if (
      form.items.length <= 1
    ) {
      return;
    }

    setForm(
      (previous) => ({
        ...previous,

        items:
          previous.items.filter(
            (
              _,
              itemIndex
            ) =>
              itemIndex !==
              index
          ),
      })
    );
  };

  /*
  |--------------------------------------------------------------------------
  | SUBMIT
  |--------------------------------------------------------------------------
  */

  const handleSubmit = async (
    event
  ) => {
    event.preventDefault();

    const validationErrors =
      validateCreateOffer(form);

    if (
      Object.keys(
        validationErrors
      ).length
    ) {
      setErrors(
        validationErrors
      );

      return;
    }

    await onSubmit(form);
  };

  /*
  |--------------------------------------------------------------------------
  | DEBUG
  |--------------------------------------------------------------------------
  */

  console.log(
    "OfferForm offerings:",
    offerings
  );

  console.log(
    "OfferForm offeringOptions:",
    offeringOptions
  );

  return (
    <form
      onSubmit={handleSubmit}
      className="offer-form"
    >
      {/* ------------------------------------------------------------------ */}
      {/* OFFER INFORMATION */}
      {/* ------------------------------------------------------------------ */}

      <div className="offer-form__section">
        <h2>
          Offer Information
        </h2>

        <div className="offer-form__grid">
          <Select
            label="Query"
            name="queryId"
            value={
              form.queryId
            }
            onChange={
              handleChange
            }
            options={
              queryOptions
            }
            placeholder="Select query"
            error={
              errors.queryId ||
              serverErrors.queryId
            }
            required
          />

          <Input
            label="Valid Until"
            type="date"
            name="validUntil"
            value={
              form.validUntil
            }
            onChange={
              handleChange
            }
            error={
              errors.validUntil ||
              serverErrors.validUntil
            }
          />

          <Input
            label="Currency"
            name="currency"
            value={
              form.currency
            }
            onChange={
              handleChange
            }
            maxLength={3}
            error={
              errors.currency ||
              serverErrors.currency
            }
          />

          <Input
            label="Offer Discount"
            type="number"
            min="0"
            step="0.01"
            name="discountAmount"
            value={
              form.discountAmount
            }
            onChange={
              handleChange
            }
            error={
              errors.discountAmount ||
              serverErrors.discountAmount
            }
          />

          <Input
            label="Offer Tax"
            type="number"
            min="0"
            step="0.01"
            name="taxAmount"
            value={
              form.taxAmount
            }
            onChange={
              handleChange
            }
            error={
              errors.taxAmount ||
              serverErrors.taxAmount
            }
          />
        </div>

        <div className="offer-form__notes">
          <label htmlFor="offer-notes">
            Notes
          </label>

          <textarea
            id="offer-notes"
            name="notes"
            value={
              form.notes
            }
            onChange={
              handleChange
            }
            rows={4}
            placeholder="Add offer notes..."
          />

          {errors.notes && (
            <ErrorMessage
              message={
                errors.notes
              }
            />
          )}
        </div>
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* OFFER ITEMS */}
      {/* ------------------------------------------------------------------ */}

      <div className="offer-form__section">
        <div className="offer-form__section-header">
          <div>
            <h2>
              Offer Items
            </h2>

            <p>
              Add the products or
              services included in
              this offer.
            </p>
          </div>

          <Button
            type="button"
            variant="secondary"
            onClick={
              addItem
            }
          >
            Add Item
          </Button>
        </div>

        <div className="offer-form__items">
          {form.items.map(
            (
              item,
              index
            ) => (
              <OfferItemForm
                key={`offer-item-${index}`}
                index={index}
                value={item}
                onChange={
                  handleItemChange
                }
                onRemove={
                  removeItem
                }
                canRemove={
                  form.items.length >
                  1
                }
                errors={
                  errors
                }
                offeringOptions={
                  offeringOptions
                }
                fetchPricingPlans={
                  fetchPricingPlans
                }
              />
            )
          )}
        </div>
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* ITEMS TABLE */}
      {/* ------------------------------------------------------------------ */}

      <OfferItemsTable
        items={
          form.items
        }
        currency={
          form.currency
        }
      />

      {/* ------------------------------------------------------------------ */}
      {/* ACTIONS */}
      {/* ------------------------------------------------------------------ */}

      <div className="offer-form__actions">
        <Button
          type="submit"
          loading={
            loading
          }
          disabled={
            loading
          }
        >
          {
            submitLabel
          }
        </Button>
      </div>
    </form>
  );
};

export default OfferForm;
